import { createColumnHelper } from '@tanstack/react-table'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactResponse } from 'models/contacts'
import { useMemo } from 'react'
import { formatDateWithTimeAndMinutes } from 'utils/formatting'
import { SEQUENCE_COLUMNS } from './sequenceColumnInfo'

const CHIP_BASE =
  'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium'

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  in_sequence: {
    label: 'Active',
    className: 'bg-blue-100 text-blue-800',
  },
  sequence_finished: {
    label: 'Completed',
    className: 'bg-green-100 text-green-800',
  },
  paused: {
    label: 'Paused',
    className: 'bg-yellow-100 text-yellow-800',
  },
  unsubscribed: {
    label: 'Unsubscribed',
    className: 'bg-orange-100 text-orange-800',
  },
  bounced: {
    label: 'Bounced',
    className: 'bg-red-100 text-red-800',
  },
  never_contacted: {
    label: 'Never Contacted',
    className: 'bg-gray-100 text-gray-800',
  },
  replied: {
    label: 'Replied',
    className: 'bg-purple-100 text-purple-800',
  },
}

const NOT_IN_SEQUENCE_CONFIG = {
  label: 'Not in Sequence',
  className: 'bg-yellow-100 text-yellow-800',
}

const REPLY_STATUS_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  interested: {
    label: 'Interested',
    className: 'bg-green-100 text-green-800',
  },
  replied: {
    label: 'Replied',
    className: 'bg-purple-100 text-purple-800',
  },
  no_reply: {
    label: 'No Reply',
    className: 'bg-gray-100 text-gray-800',
  },
}

function SequenceStatusChip({ status }: { status: string | null | undefined }) {
  const config = status
    ? STATUS_CONFIG[status] || {
        label: status,
        className: 'bg-gray-100 text-gray-800',
      }
    : NOT_IN_SEQUENCE_CONFIG

  return (
    <span className={`${CHIP_BASE} ${config.className}`}>{config.label}</span>
  )
}

function getReplyConfig(row: ContactResponse): {
  label: string
  className: string
} {
  if (!row.seq_membership_status || row.seq_membership_status !== 'replied') {
    return REPLY_STATUS_CONFIG.no_reply
  }
  if (row.seq_has_interested) return REPLY_STATUS_CONFIG.interested
  return REPLY_STATUS_CONFIG.replied
}

function ReplyStatusChip({ row }: { row: ContactResponse }) {
  const config = getReplyConfig(row)
  return (
    <span className={`${CHIP_BASE} ${config.className}`}>{config.label}</span>
  )
}

function OpenedChip({ opened }: { opened: boolean }) {
  const className = opened
    ? 'bg-cyan-100 text-cyan-800'
    : 'bg-gray-100 text-gray-800'
  const label = opened ? 'Opened' : 'Not Opened'
  return <span className={`${CHIP_BASE} ${className}`}>{label}</span>
}

function NumericCell({ value }: { value: number | null | undefined }) {
  if (value == null)
    return <span className="text-fg-secondary">{'\u2013'}</span>
  return <span className="tabular-nums">{value}</span>
}

export function useSequenceColumns() {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  const columnHelper = createColumnHelper<ContactResponse>()
  const seqColumnHelper = createTableColumnHelper({
    columns: SEQUENCE_COLUMNS,
    tooltips,
  })

  return useMemo(
    () => [
      columnHelper.display({
        ...(seqColumnHelper.retrieveColumn('seq_membership_status') as any),
        id: 'seq_membership_status',
        size: 160,
        cell: (info: any) => {
          const row = info.row.original
          return <SequenceStatusChip status={row.seq_membership_status} />
        },
      }),
      columnHelper.accessor('seq_scheduled_emails', {
        ...(seqColumnHelper.retrieveColumn('seq_scheduled_emails') as any),
        size: 150,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_emails_sent', {
        ...(seqColumnHelper.retrieveColumn('seq_emails_sent') as any),
        size: 120,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_opens', {
        ...(seqColumnHelper.retrieveColumn('seq_opens') as any),
        size: 120,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_replies', {
        ...(seqColumnHelper.retrieveColumn('seq_replies') as any),
        size: 120,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_last_email', {
        ...(seqColumnHelper.retrieveColumn('seq_last_email') as any),
        size: 200,
        cell: (info: any) => {
          const value = info.getValue()
          if (!value)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return (
            <span className="text-sm tabular-nums">
              {formatDateWithTimeAndMinutes(new Date(value))}
            </span>
          )
        },
      }),
      columnHelper.display({
        ...(seqColumnHelper.retrieveColumn('seq_sequence_step') as any),
        id: 'seq_sequence_step',
        size: 150,
        cell: (info: any) => {
          const row = info.row.original
          if (row.seq_steps_completed == null || row.seq_total_steps == null)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return (
            <span className="text-sm">
              <span className="font-medium tabular-nums">
                {row.seq_steps_completed}
              </span>
              <span className="text-fg-secondary">
                {' '}
                of {row.seq_total_steps}
              </span>
            </span>
          )
        },
      }),
      columnHelper.display({
        ...(seqColumnHelper.retrieveColumn('seq_reply_status') as any),
        id: 'seq_reply_status',
        size: 140,
        cell: (info: any) => {
          const row = info.row.original
          if (row.seq_membership_status == null)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return <ReplyStatusChip row={row} />
        },
      }),
      columnHelper.accessor('seq_opened', {
        ...(seqColumnHelper.retrieveColumn('seq_opened') as any),
        size: 120,
        cell: (info: any) => {
          const value = info.getValue()
          if (value == null)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return <OpenedChip opened={value} />
        },
      }),
    ],
    [preferences, tooltips]
  )
}
